//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import angular, { IScope } from 'angular';

import template from './uia-bulk-actions.html';

import { FeatureNames } from '@bvengineering/curalate-features';
import { CreatorsCampaignsTrackingIds } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTrackingId';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  getStateName,
  getStateParams,
} from '@react-bridge/wrappers/navigation';
import { BulkActionNames } from 'app/bulk-actions/model/BulkActionNames';
import { ICollection } from 'app/bulk-actions/model/ICollection';
import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';
import { hasFeature } from 'app/initialization/routing/stateHelpers';
import {
  captionTooltip,
  muteCaptionDisclaimerMessage,
  muteTooltipMessage,
} from 'app/constants/mute-tooltip-content';
import { RoutableStateName } from 'app/top-level-states';
import { INgRedux } from 'ng-redux';

import '@galleries/components/modals/bulk-add-to-galleries/BulkAddToGalleriesModalWrapped';
import '@galleries/components/modals/bulk-add-labels/BulkActionLabelsModal';
import '@galleries/components/modals/no-galleries/NoGalleriesModal';
import '@galleries/components/modals/rights/RequestRightsAtMentioned';
import '@galleries/components/modals/rights/RequestRightsNonMentioned';
import '@galleries/components/modals/rights/RightsOwnedModal';
import { AI_MODERATION_BULK_REJECT_PAGES } from '@media-library/ai-content-curation/constants/AiContentCurationConstants';
import '@media-library/ai-content-curation/components/BulkActionsBulkReject';
import 'app/bulk-actions/bulk-actions-add-to-collection-modal/bulk-actions-add-to-collection-modal';
import 'app/bulk-actions/components/BulkAddToStreamWrapped';
import 'app/bulk-actions/bulk-actions-confirmation-modal/bulk-actions-confirmation-modal';
import 'app/bulk-actions/bulk-actions-remove-from-connect-modal/bulk-actions-remove-from-connect-modal';
import 'app/bulk-actions/bulk-actions-request-review-now-modal/bulk-actions-request-review-now-modal';
import 'app/bulk-actions/uia-bulk-actions/uia-bulk-actions.less';
import 'app/bulk-actions/uia-bulk-actions/TikTokLike2BuyBulkActions';
import 'app/bulk-actions/uia-bulk-actions/DeleteFromLibraryConfirmationModal';
import 'app/bulk-actions/uia-bulk-actions/UiaDeleteContentModal';
import 'app/bulk-actions/uia-bulk-actions/UiaReportAiContentModal';

import { FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID } from '@galleries/constants/galleriesConstants';
import { hasLike2BuyModerationPermission } from '@instagram-account/redux/like2buy/like2buyModerationModel';
import { getIsMediaLibraryStreamsEnabled } from '@media-library/redux/getMediaLibraryPage';
import '@app/bulk-actions/components/ModeratorBulkActions';
import { getModeratorBulkActionsAccess } from '@app/bulk-actions/model/moderatorBulkActionsAccess';
import { SessionMode } from '@moderation-mode/constants/SessionMode';
import { sessionModeStore } from '@moderation-mode/sessionModeStore';

export class UiaBulkActions {
  // Bindings
  public disabledActions: string[];
  public isCollectionsEnabled: boolean;
  public isInstaShopEnabled: boolean;
  public isLabelsEnabled: boolean;
  public isLibraryEnabled: boolean;
  public isMediaLibrarySourcedOrganicContentEnabled: boolean;
  public isShareV2Vendor: boolean;
  public isGalleriesEnabled: boolean;
  public isRightsRequestsBulkActionsEnabled: boolean;
  public isInstaShopClient: boolean;
  public currentCollection: ICollection | null;
  public currentGallery: string;
  public isBazaarvoiceSyndicationEnabled: boolean;
  public isUiaBulkVideoClosedCaptionsEnabled: boolean;
  public isUiaBulkVideoMuteEnabled: boolean;
  public isBazaarvoiceNetworkSyndicationOnlyFullClient: boolean;
  public selectedItems: ISelectedItem[];
  // TODO: PD-319070 - Remove this field and its constructor init once media_library_streams is released
  public isStreamsExperienceEnabled: boolean = false;

  public BulkActionNames;
  public CreatorsCampaignsTrackingIds;
  public SamplingCampaignsTrackingIds;

  public permissionsTooltipContent = `You don't have permission for this action. Contact a group admin if this is a mistake.`;

  private unsubscribeMode: () => void;
  private unsubscribeSession: () => void;

  constructor(
    private $ngRedux: INgRedux,
    private $scope: IScope,
  ) {
    this.BulkActionNames = BulkActionNames;
    this.CreatorsCampaignsTrackingIds = CreatorsCampaignsTrackingIds;
    this.SamplingCampaignsTrackingIds = SAMPLING_CAMPAIGNS_TRACKING_ID;
    this.isStreamsExperienceEnabled = getIsMediaLibraryStreamsEnabled(
      this.$ngRedux.getState(),
    );
  }

  public $onInit(): void {
    const refresh = () => this.$scope.$evalAsync();
    let session = this.$ngRedux.getState().session;
    this.unsubscribeMode = sessionModeStore.subscribe(refresh);
    const unsubscribeSession = this.$ngRedux.subscribe(() => {
      const nextSession = this.$ngRedux.getState().session;
      if (nextSession !== session) {
        session = nextSession;
        refresh();
      }
    });
    this.unsubscribeSession = () => {
      unsubscribeSession();
    };
  }

  public $onDestroy(): void {
    this.unsubscribeMode?.();
    this.unsubscribeSession?.();
  }

  private getModeratorAccess() {
    return getModeratorBulkActionsAccess(
      this.$ngRedux.getState().session,
      sessionModeStore.getMode() === SessionMode.Moderator,
      getStateName(),
    );
  }

  public isModeratorBulkActionsEnabled(): boolean {
    return this.getModeratorAccess().isEnabled;
  }

  public isModeratorWriteDisabled(): boolean {
    const access = this.getModeratorAccess();
    return access.isEnabled && !access.canWrite;
  }

  public isBulkActionAvailable(action: string): boolean {
    return this.disabledActions.indexOf(action) === -1;
  }

  public showAddToCollection(): boolean {
    return (
      this.isBulkActionAvailable(this.BulkActionNames.ADD_TO_COLLECTION) &&
      this.isCollectionsEnabled &&
      this.isMediaLibrarySourcedOrganicContentEnabled
    );
  }

  public showAddLabels(): boolean {
    return (
      this.isBulkActionAvailable(this.BulkActionNames.ADD_LABELS) &&
      this.isLabelsEnabled
    );
  }

  public showAddToLibrary(): boolean {
    return (
      this.isBulkActionAvailable(this.BulkActionNames.ADD_TO_LIBRARY) &&
      this.isLibraryEnabled
    );
  }

  public showRemoveFromConnect(): boolean {
    return (
      this.isBulkActionAvailable(this.BulkActionNames.REMOVE_FROM_CONNECT) &&
      this.isShareV2Vendor &&
      !this.isBazaarvoiceSyndicationEnabled
    );
  }

  public showRemoveLabels(): boolean {
    return (
      this.isBulkActionAvailable(this.BulkActionNames.REMOVE_LABELS) &&
      this.isLabelsEnabled
    );
  }

  public showAddToGallery(): boolean {
    return (
      this.isBulkActionAvailable(this.BulkActionNames.ADD_TO_GALLERY) &&
      this.isGalleriesEnabled
    );
  }

  public showRemoveFromGallery(): boolean {
    const currentStateName = getStateName();
    const isOnGalleriesPage =
      currentStateName === 'galleries' || currentStateName === 'instashop';
    return (
      isOnGalleriesPage &&
      !!this.currentGallery &&
      this.currentGallery !== FAKE_BAZAARVOICE_NETWORK_CONTAINER_ID
    );
  }

  public showAddToInstaShop(): boolean {
    const acceptableStates = new Set<RoutableStateName>([
      'instagramAccount.summary',
      'instagramAccount.feedEngagement',
    ]);
    const onInstagramDashboard = acceptableStates.has(getStateName());

    return (
      this.isBulkActionAvailable(this.BulkActionNames.ADD_TO_GALLERY) &&
      this.isInstaShopClient &&
      onInstagramDashboard
    );
  }

  public showAddButton(): boolean {
    if (this.areGoogleAssetFeedActionsEnabled()) {
      return false;
    } else {
      return (
        (this.showAddLabels() ||
          this.showAddToCollection() ||
          this.showAddToLibrary() ||
          this.showAddToGallery() ||
          this.showAddToInstaShop()) &&
        !this.isShareV2Vendor
      );
    }
  }

  public showRemoveButton(): boolean {
    return true;
  }

  public showRequestReviewNow(): boolean {
    return (
      this.isBulkActionAvailable(this.BulkActionNames.REQUEST_REVIEW_NOW) &&
      this.isShareV2Vendor &&
      !this.isBazaarvoiceSyndicationEnabled
    );
  }

  public showRequestRights(): boolean {
    const currentStateName = getStateName();
    const isOnGalleriesPage =
      currentStateName === 'galleries' || currentStateName === 'instashop';
    return isOnGalleriesPage && !this.isShareV2Vendor;
  }

  public disclaimerMessage(): string {
    if (
      this.isUiaBulkVideoMuteEnabled &&
      this.isUiaBulkVideoClosedCaptionsEnabled
    ) {
      return muteCaptionDisclaimerMessage;
    }
    if (this.isUiaBulkVideoMuteEnabled) {
      return muteTooltipMessage;
    }
    return captionTooltip;
  }

  public showVideoPopover(): boolean {
    if (this.getModeratorAccess().isAllSharedMedia) {
      return false;
    }
    if (
      !this.isUiaBulkVideoMuteEnabled &&
      !this.isUiaBulkVideoClosedCaptionsEnabled
    ) {
      return false;
    }
    const currentStateName = getStateName();
    const mediaLibrary = currentStateName.split('.')[0];
    const isOnGalleriesPage =
      currentStateName === 'galleries' || mediaLibrary === 'library';
    return isOnGalleriesPage || this.isShareV2Vendor;
  }

  public showLike2BuyActions(): boolean {
    return getStateName() === 'instagramAccount.like2buy';
  }

  public showTikTokLike2BuyActions(): boolean {
    return getStateName() === 'tiktok.like2buy.moderation';
  }

  public showBulkReject(): boolean {
    return AI_MODERATION_BULK_REJECT_PAGES.has(getStateName());
  }

  public isLike2BuyActionDisabled(): boolean {
    return !hasLike2BuyModerationPermission();
  }

  public getCampaignSlugName(): string | undefined {
    const stateParams = getStateParams<{ campaignSlugName: string }>();
    return stateParams.campaignSlugName;
  }

  public showDeleteCreatorCampaignContent(): boolean {
    return this.isBulkActionAvailable(
      this.BulkActionNames.DELETE_CREATOR_CAMPAIGN_CONTENT,
    );
  }

  public showReportAiContent(): boolean {
    // TODO: PD-331028 - Remove Client Flagged content feature check once released
    return (
      this.isBulkActionAvailable(this.BulkActionNames.REPORT_AI_CONTENT) &&
      hasFeature(FeatureNames.ClientFlaggedContent)
    );
  }

  private areGoogleAssetFeedActionsEnabled(): boolean {
    return (
      this.isBulkActionAvailable(
        this.BulkActionNames.REMOVE_FROM_GOOGLE_ASSET_FEED,
      ) ||
      this.isBulkActionAvailable(
        this.BulkActionNames.PUBLISH_TO_GOOGLE_ASSET_FEED,
      )
    );
  }
}

angular.module('curalate').component('uiaBulkActions', {
  bindings: {
    confirmationModal: '<',
    currentCollection: '<',
    currentGallery: '<',
    disabledActions: '<',
    isAddButtonLoading: '<',
    isVideoActionInProgress: '<',
    isCollectionsEnabled: '<',
    isDownloadDisabled: '&',
    isLabelsEnabled: '<',
    isLibraryEnabled: '<',
    isGalleriesEnabled: '<',
    isInstaShopClient: '<',
    isInstaShopEnabled: '<',
    hasMultipleInstaShops: '<',
    isShareV2Vendor: '<',
    isRightRequestAtMentionedModalOpen: '<',
    isRightsRequestsNonAtMentionedModalOpen: '<',
    isBazaarvoiceNetworkSyndicationOnlyFullClient: '<',
    isMediaLibrarySourcedOrganicContentEnabled: '<',
    onAddLabels: '&',
    onAddToLibrary: '&',
    onAddToCollection: '&',
    onClearSelectedItems: '&',
    onClickCaptionTurnOnOff: '&',
    onCloseConfirmationModal: '&',
    onDeleteFromLike2buy: '&',
    onDownloadTriggered: '&',
    onHideFromLike2buy: '&',
    onOpenConfirmationModal: '&',
    onRemoveFromCollection: '&',
    onRemoveFromConnect: '&',
    onRemoveFromGallery: '&',
    onRemoveFromGoogleAssetFeed: '&',
    onPinToLike2buy: '&',
    onPublishToGoogleAssetFeed: '&',
    onRemoveFromLibrary: '&',
    onRemoveLabels: '&',
    onRequestReviewNow: '&',
    selectedItems: '<',
    onRequestRights: '&',
    isLoadingRequestRights: '<',
    onClickAddLabels: '&',
    onClickAddToGalleries: '&',
    onClickAddToInstaShop: '&',
    onClickRemoveLabels: '&',
    isBazaarvoiceSyndicationEnabled: '<',
    isUiaBulkVideoClosedCaptionsEnabled: '<',
    isUiaBulkVideoMuteEnabled: '<',
    onClickMuteUnmuteSelectedItems: '&',
  },
  controller: ['$ngRedux', '$scope', UiaBulkActions],
  template,
});
